/**
 * Code 128 barcode encoding, with no dependencies.
 *
 * WHY not JsBarcode: a document number has to become a barcode on paper coming
 * out of four unrelated places — the web print page, the Tauri till, the mandob
 * app's web export, and the server's PDFKit invoice. JsBarcode is browser-only
 * and is a dependency of `apps/web` alone; the till and the field app are
 * standalone packages deliberately kept outside the workspace, and adding a
 * package to either has broken a boot here before. Code 128 is a small, exactly
 * specified symbology, so encoding it directly is less code than making one
 * library reach all four surfaces — and it means the bars on the shelf label,
 * the receipt, and the PDF are produced by the same table.
 *
 * Code 128 is the right symbology for this: EAN/UPC only carry digits of a fixed
 * length, and a document number is `INV-2026-00042`. Code 128 carries the full
 * printable ASCII range, and encodes runs of digits at double density.
 *
 * The canonical copy lives here. `apps/pos-desktop` and `apps/mandob-mobile`
 * carry byte-identical copies for the same reason their `lib/receipt.ts` files
 * do — they cannot import from this package.
 */

/**
 * Bar/space widths in modules for every Code 128 symbol, 0-106.
 * Each entry is six digits: bar, space, bar, space, bar, space.
 * Index 106 is the stop pattern, which carries a seventh bar.
 */
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
];

const START_B = 104;
const START_C = 105;
const CODE_B = 100;
const CODE_C = 99;
const STOP = 106;

/** Code 128 B encodes ASCII 32-126 as value = char - 32. */
function valueB(ch: string): number {
  const code = ch.charCodeAt(0);
  return code - 32;
}

/** How many digits start at `i` — a run of 4+ is worth switching to set C for. */
function digitRun(input: string, i: number): number {
  let n = 0;
  while (i + n < input.length && input[i + n]! >= '0' && input[i + n]! <= '9') n += 1;
  return n;
}

/**
 * Encode a string to Code 128 symbol values, including start, checksum and stop.
 *
 * Sets B and C only: B covers every printable character a document number can
 * contain, and C halves the width of digit runs, which is most of a typical
 * number. Set A exists for control characters, which never appear here.
 *
 * Returns null when the input holds a character Code 128 B cannot express, so
 * callers can leave the barcode off rather than print an unscannable one.
 */
export function encodeCode128(input: string): number[] | null {
  if (!input) return null;
  for (const ch of input) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code > 126) return null;
  }

  const codes: number[] = [];
  let i = 0;
  // An even-length digit run at the very start is cheaper in set C from symbol
  // one; anything else starts in B. (Set C consumes digits in PAIRS, so an odd
  // run has to spend a symbol switching back for the last digit — worth it from
  // 4 digits up mid-string, but not worth starting in C for.)
  let inC = digitRun(input, 0) >= 4 && digitRun(input, 0) % 2 === 0;
  codes.push(inC ? START_C : START_B);

  while (i < input.length) {
    const run = digitRun(input, i);
    if (inC) {
      if (run >= 2) {
        codes.push(Number(input.slice(i, i + 2)));
        i += 2;
        continue;
      }
      codes.push(CODE_B);
      inC = false;
      continue;
    }
    // Switching costs one symbol and saves one per digit pair, so it pays from
    // four digits — or from two when they run to the end of the string.
    if (run >= 4 || (run >= 2 && i + run === input.length && run % 2 === 0)) {
      codes.push(CODE_C);
      inC = true;
      continue;
    }
    codes.push(valueB(input[i]!));
    i += 1;
  }

  // Modulo-103 checksum: start value plus each symbol weighted by its position.
  let sum = codes[0]!;
  for (let k = 1; k < codes.length; k += 1) sum += codes[k]! * k;
  codes.push(sum % 103);
  codes.push(STOP);
  return codes;
}

/** Alternating bar/space module widths for the whole symbol, starting with a bar. */
export function code128Modules(input: string): number[] | null {
  const codes = encodeCode128(input);
  if (!codes) return null;
  const widths: number[] = [];
  for (const c of codes) {
    for (const d of PATTERNS[c] as string) widths.push(Number(d));
  }
  return widths;
}

export interface Code128Options {
  /** Width of one module in user units. Default 1. */
  moduleWidth?: number;
  /** Height of the bars, excluding the caption. Default 40. */
  height?: number;
  /** Print the value underneath. Default true. */
  displayValue?: boolean;
  /** Caption size. Default 10. */
  fontSize?: number;
  /** Quiet zone either side, in modules. The spec's minimum is 10. Default 10. */
  quietModules?: number;
  /** Bar colour. Default black. */
  lineColor?: string;
  /** Background. Empty string leaves it transparent — right on a tinted header. */
  background?: string;
  /** Caption font stack. */
  fontFamily?: string;
}

/**
 * Render a Code 128 barcode as a standalone SVG string.
 *
 * A string, not a DOM node, because every consumer injects it as markup: the
 * print pages build an HTML document to hand to `window.print()`, and the till
 * and field app do the same for their receipts. Returns null for an
 * unencodable (or empty) value, so a document with no number yet — an offline
 * sale, a draft — simply prints without one.
 */
export function code128Svg(value: string, options: Code128Options = {}): string | null {
  const modules = code128Modules(value);
  if (!modules) return null;

  const mw = options.moduleWidth ?? 1;
  const height = options.height ?? 40;
  const showText = options.displayValue !== false;
  const fontSize = options.fontSize ?? 10;
  const quiet = options.quietModules ?? 10;
  const color = options.lineColor ?? '#000000';
  const background = options.background ?? '#ffffff';
  const font = options.fontFamily ?? 'monospace';
  // The caption sits below the bars with a little air, so it never touches them.
  const textBand = showText ? fontSize + 4 : 0;

  const totalModules = modules.reduce((a, b) => a + b, 0) + quiet * 2;
  const width = totalModules * mw;
  const totalHeight = height + textBand;

  let x = quiet * mw;
  let bars = '';
  // Widths alternate bar, space, bar, … — only the bars are drawn.
  for (let i = 0; i < modules.length; i += 1) {
    const w = modules[i]! * mw;
    if (i % 2 === 0) bars += `<rect x="${round(x)}" y="0" width="${round(w)}" height="${round(height)}"/>`;
    x += w;
  }

  const bg = background
    ? `<rect x="0" y="0" width="${round(width)}" height="${round(totalHeight)}" fill="${background}"/>`
    : '';
  const caption = showText
    ? `<text x="${round(width / 2)}" y="${round(totalHeight - 1)}" text-anchor="middle"`
      + ` font-family="${font}" font-size="${fontSize}" fill="${color}"`
      + ` letter-spacing="1">${escapeXml(value)}</text>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${round(width)}" height="${round(totalHeight)}"`
    + ` viewBox="0 0 ${round(width)} ${round(totalHeight)}" shape-rendering="crispEdges" role="img"`
    + ` aria-label="${escapeXml(value)}">${bg}<g fill="${color}">${bars}</g>${caption}</svg>`;
}

/** Trim float noise so the markup stays small and diffs stay readable. */
function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
